import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Tabs,
  Space,
  Button,
  Statistic,
  Select,
  DatePicker,
  Table,
  Tag,
  Progress,
  Spin,
  Empty,
  Alert,
  Tooltip,
  Typography,
} from "antd";
import {
  SyncOutlined,
  DownloadOutlined,
  DashboardOutlined,
  PieChartOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  RiseOutlined,
  FallOutlined,
  TeamOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useReports } from "../../../hooks/finance/useReports";
import { useBudget } from "../../../hooks/finance/useBudget";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const revenueBandStyle = {
  padding: "18px 8px",
};

const MetricTitle = ({ children, explanation }) => (
  <Space size={5} align="center">
    <span>{children}</span>
    <Tooltip title={explanation}>
      <span
        aria-label={`About ${children}`}
        role="img"
        tabIndex={0}
        style={{ color: "#8c8c8c", cursor: "help", lineHeight: 1 }}
      >
        <InfoCircleOutlined />
      </span>
    </Tooltip>
  </Space>
);

// Generate academic years (single year format, e.g. "2026")
const currentYear = new Date().getFullYear();
const academicYears = Array.from({ length: 5 }, (_, i) =>
  String(currentYear - 2 + i),
);

const FinancialReports = () => {
  const {
    loading,
    dashboardData,
    budgetVsActualData,
    revenueData,
    payrollData,
    expenditureData,
    fetchDashboard,
    fetchBudgetVsActual,
    fetchRevenue,
    fetchPayroll,
    fetchExpenditure,
  } = useReports();

  const { departments, fetchDepartments } = useBudget();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [filters, setFilters] = useState({
    acadyr: String(currentYear),
    departmentId: null,
    dateRange: null,
    term: null,
  });

  // Revenue-specific filters
  const [revenueFilters, setRevenueFilters] = useState({
    acadyr: String(currentYear),
    dateRange: null,
  });

  // Payroll-specific filters
  const [payrollFilters, setPayrollFilters] = useState({
    year: String(currentYear),
    departmentId: null,
    month: null, // dayjs object or null
  });

  // Expenditure-specific filters
  const [expenditureFilters, setExpenditureFilters] = useState({
    fiscalYear: String(currentYear),
    departmentId: null,
  });

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload data when tab changes
  useEffect(() => {
    loadReportData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Reload data when filters change
  useEffect(() => {
    loadReportData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.acadyr, filters.departmentId, filters.dateRange, filters.term]);

  // Reload revenue data when revenue filters change
  useEffect(() => {
    if (activeTab === "revenue") {
      fetchRevenue({
        acadyr: revenueFilters.acadyr,
        dateFrom: revenueFilters.dateRange?.[0]?.format("YYYY-MM-DD"),
        dateTo: revenueFilters.dateRange?.[1]?.format("YYYY-MM-DD"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenueFilters.acadyr, revenueFilters.dateRange, activeTab]);

  // Reload expenditure data when expenditure filters change
  useEffect(() => {
    if (activeTab === "expenditure") {
      fetchExpenditure({
        fiscalYear: expenditureFilters.fiscalYear,
        departmentId: expenditureFilters.departmentId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expenditureFilters.fiscalYear,
    expenditureFilters.departmentId,
    activeTab,
  ]);

  // Reload payroll data when payroll filters change
  useEffect(() => {
    if (activeTab === "payroll") {
      fetchPayroll({
        year: payrollFilters.year,
        departmentId: payrollFilters.departmentId,
        month: payrollFilters.month
          ? payrollFilters.month.format("YYYY-MM")
          : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payrollFilters.year,
    payrollFilters.departmentId,
    payrollFilters.month,
    activeTab,
  ]);

  const loadReportData = (tab) => {
    const fiscalYear = filters.acadyr;

    switch (tab) {
      case "dashboard":
        fetchDashboard({ fiscalYear, acadyr: filters.acadyr });
        break;
      case "budget":
        fetchBudgetVsActual({ fiscalYear, term: filters.term });
        break;
      case "revenue":
        fetchRevenue({
          acadyr: revenueFilters.acadyr,
          dateFrom: revenueFilters.dateRange?.[0]?.format("YYYY-MM-DD"),
          dateTo: revenueFilters.dateRange?.[1]?.format("YYYY-MM-DD"),
        });
        break;
      case "payroll":
        fetchPayroll({
          year: payrollFilters.year,
          departmentId: payrollFilters.departmentId,
          month: payrollFilters.month
            ? payrollFilters.month.format("YYYY-MM")
            : null,
        });
        break;
      case "expenditure":
        fetchExpenditure({
          fiscalYear: expenditureFilters.fiscalYear,
          departmentId: expenditureFilters.departmentId,
        });
        break;
      default:
        break;
    }
  };

  const handleRefresh = () => {
    loadReportData(activeTab);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const outstandingValueStyle = (amount, additionalStyles = {}) => ({
    ...additionalStyles,
    color: parseFloat(amount || 0) > 0 ? "#ff4d4f" : "#52c41a",
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "Over Budget":
        return "red";
      case "Near Limit":
        return "orange";
      case "On Track":
        return "green";
      case "Under Utilized":
        return "blue";
      default:
        return "default";
    }
  };

  // Dashboard Tab Content
  const DashboardContent = () => {
    if (!dashboardData) {
      return <Empty description="No dashboard data available" />;
    }

    const { revenue, budget, requisitions } = dashboardData;

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Revenue Section */}
        <Card
          title={
            <>
              <DollarOutlined /> Revenue Overview
            </>
          }
          size="small"
        >
          <div style={{ width: "100%" }}>
            <section
              aria-labelledby="revenue-collected-heading"
              style={{
                ...revenueBandStyle,
                paddingTop: 2,
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <Text strong id="revenue-collected-heading">
                  Collected
                </Text>
              </div>
              <Row gutter={[32, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title={
                      <MetricTitle explanation="All valid payments received. This always equals Tuition Collected + Transport Collected + Others.">
                        Total Collected
                      </MetricTitle>
                    }
                    value={formatCurrency(revenue.totalCollected)}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title={
                      <MetricTitle explanation="The portion of valid payments allocated to tuition fees.">
                        Tuition Collected
                      </MetricTitle>
                    }
                    value={formatCurrency(revenue.tuitionCollected)}
                    prefix="UGX"
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title={
                      <MetricTitle explanation="The portion of valid payments allocated to transport fees.">
                        Transport Collected
                      </MetricTitle>
                    }
                    value={formatCurrency(revenue.transportCollected)}
                    prefix="UGX"
                  />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Valid collections for fee types other than tuition and transport, including BoT, admissions, and other school fees.">
                        Others
                      </MetricTitle>
                    }
                    value={formatCurrency(revenue.othersCollected)}
                    prefix="UGX"
                  />
                </Col>
              </Row>
            </section>

            <section
              aria-labelledby="revenue-outstanding-heading"
              style={{
                ...revenueBandStyle,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <Text strong id="revenue-outstanding-heading">
                  Outstanding
                </Text>
              </div>
              <Row gutter={[32, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Unpaid tuition carried from terms earlier than the current term. It is included in Outstanding Fees: Tuition.">
                        Balance Brought Forward: Tuition
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.balanceBroughtForwardTuition ??
                        revenue.balanceBroughtForward ??
                        0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.balanceBroughtForwardTuition ??
                        revenue.balanceBroughtForward,
                    )}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Prior-term tuition
                  </Text>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Unpaid transport carried from terms earlier than the current term. It is included in Outstanding Fees: Transport.">
                        Balance Brought Forward: Transport
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.balanceBroughtForwardTransport || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.balanceBroughtForwardTransport,
                    )}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Prior-term transport
                  </Text>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Unpaid tuition from prior terms plus the current term. Future-term assignments are excluded until that term becomes current.">
                        Outstanding Fees: Tuition
                      </MetricTitle>
                    }
                    value={formatCurrency(revenue.tuitionOutstanding || 0)}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.tuitionOutstanding,
                    )}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Prior plus current tuition
                  </Text>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Unpaid transport from prior terms plus the current term. Future-term assignments are excluded until that term becomes current.">
                        Outstanding Fees: Transport
                      </MetricTitle>
                    }
                    value={formatCurrency(revenue.transportOutstanding || 0)}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.transportOutstanding,
                    )}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Prior plus current transport
                  </Text>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Prior- and current-term tuition plus transport outstanding, before prepayments are deducted. Future terms are excluded.">
                        Gross Outstanding Fees (Tuition + Transport)
                      </MetricTitle>
                    }
                    value={formatCurrency(revenue.grossOutstandingFees || 0)}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.grossOutstandingFees,
                    )}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Tuition plus transport
                  </Text>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Gross tuition and transport outstanding after the available tuition and transport prepayments are deducted.">
                        Net Outstanding Fees
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.netOutstandingFees ?? revenue.netOutstanding ?? 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.netOutstandingFees ?? revenue.netOutstanding,
                    )}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    After prepayments
                  </Text>
                </Col>
              </Row>
            </section>

            <section
              aria-labelledby="revenue-current-term-heading"
              style={{
                ...revenueBandStyle,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <Space size={8} wrap>
                  <Text strong id="revenue-current-term-heading">
                    Current Term
                  </Text>
                  {revenue.currentTerm && (
                    <Text type="secondary">({revenue.currentTerm})</Text>
                  )}
                </Space>
              </div>
              <Row gutter={[32, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Valid tuition collections received between the current term's start and end dates.">
                        Current Term Tuition Collected
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.currentTermTuitionCollected || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Valid transport collections received between the current term's start and end dates.">
                        Current Term Transport Collected
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.currentTermTransportCollected || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Valid current-term collections other than tuition and transport, including BoT, admissions, and other school fees.">
                        Current Term Others Collected
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.currentTermOthersCollected || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Unpaid tuition assigned to the current academic term only.">
                        Current Term Outstanding Tuition
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.currentTermTuitionOutstanding ??
                        revenue.currentTermOutstanding ??
                        0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.currentTermTuitionOutstanding ??
                        revenue.currentTermOutstanding,
                    )}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Unpaid transport assigned to the current academic term only.">
                        Current Term Outstanding Transport
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.currentTermTransportOutstanding || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.currentTermTransportOutstanding,
                    )}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title={
                      <MetricTitle explanation="Current-term tuition outstanding plus current-term transport outstanding.">
                        Gross Current Term Outstanding
                      </MetricTitle>
                    }
                    value={formatCurrency(
                      revenue.grossCurrentTermOutstanding || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenue.grossCurrentTermOutstanding,
                    )}
                  />
                </Col>
              </Row>
            </section>

            <section
              aria-labelledby="revenue-prepayments-heading"
              style={{
                ...revenueBandStyle,
                borderTop: "1px solid #f0f0f0",
                paddingBottom: 2,
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <Text strong id="revenue-prepayments-heading">
                  Prepayments
                </Text>
              </div>
              <Row gutter={[32, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title="B/F Prepayment: Tuition"
                    value={formatCurrency(
                      revenue.balanceBroughtForwardPrepaymentTuition || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title="B/F Prepayment: Transport"
                    value={formatCurrency(
                      revenue.balanceBroughtForwardPrepaymentTransport || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title="Current Term Prepayments: Tuition"
                    value={formatCurrency(
                      revenue.currentTermPrepaymentTuition || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title="Current Term Prepayments: Transport"
                    value={formatCurrency(
                      revenue.currentTermPrepaymentTransport || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Statistic
                    title="Gross Prepayments"
                    value={formatCurrency(revenue.grossPrepayments || 0)}
                    prefix="UGX"
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
              </Row>
            </section>
          </div>
        </Card>

        {/* Budget Section */}
        <Card
          title={
            <>
              <PieChartOutlined /> Budget Overview
            </>
          }
          size="small"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total Allocated"
                value={formatCurrency(budget.totalAllocated)}
                prefix="UGX"
                valueStyle={{ color: "#1890ff" }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total Spent"
                value={formatCurrency(budget.totalSpent)}
                prefix="UGX"
                valueStyle={{ color: "#ff4d4f" }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Committed"
                value={formatCurrency(budget.totalCommitted)}
                prefix="UGX"
                valueStyle={{ color: "#faad14" }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Available"
                value={formatCurrency(budget.available)}
                prefix="UGX"
                valueStyle={{ color: "#52c41a" }}
              />
            </Col>
          </Row>
        </Card>

        {/* Requisitions Section */}
        <Card
          title={
            <>
              <ShoppingCartOutlined /> Pending Requisitions
            </>
          }
          size="small"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Pending Approval"
                value={requisitions.pendingApproval}
                suffix="requests"
                valueStyle={{ color: "#faad14" }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Pending Value"
                value={formatCurrency(requisitions.pendingValue)}
                prefix="UGX"
              />
            </Col>
          </Row>
        </Card>
      </Space>
    );
  };

  // Budget vs Actual Tab Content
  const BudgetVsActualContent = () => {
    if (!budgetVsActualData) {
      return (
        <Empty description="No budget data available. Select a fiscal year and refresh." />
      );
    }

    const columns = [
      {
        title: "Item",
        dataIndex: "departmentName",
        key: "departmentName",
        fixed: "left",
        width: 150,
      },
      {
        title: "Budgeted",
        dataIndex: "budgetedAmount",
        key: "budgetedAmount",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Actual Spent",
        dataIndex: "actualSpent",
        key: "actualSpent",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Committed",
        dataIndex: "committed",
        key: "committed",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Variance",
        dataIndex: "variance",
        key: "variance",
        render: (val) => {
          const num = parseFloat(val);
          return (
            <span style={{ color: num >= 0 ? "#52c41a" : "#ff4d4f" }}>
              {num >= 0 ? <RiseOutlined /> : <FallOutlined />} UGX{" "}
              {formatCurrency(Math.abs(num))}
            </span>
          );
        },
        align: "right",
      },
      {
        title: "Utilization",
        dataIndex: "utilizationPercentage",
        key: "utilizationPercentage",
        render: (val) => (
          <Progress
            percent={Math.min(val, 100)}
            size="small"
            status={val > 100 ? "exception" : val > 90 ? "active" : "normal"}
          />
        ),
        width: 150,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      },
    ];

    const exportBudgetReport = () => {
      const exportData = budgetVsActualData.departments.map((d) => ({
        Item: d.departmentName,
        "Budgeted Amount": parseFloat(d.budgetedAmount || 0),
        "Actual Spent": parseFloat(d.actualSpent || 0),
        Committed: parseFloat(d.committed || 0),
        Variance: parseFloat(d.variance || 0),
        "Utilization %": d.utilizationPercentage,
        Status: d.status,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Budget vs Actual");
      const termSuffix = filters.term ? `_${filters.term}` : "_all_terms";
      XLSX.writeFile(
        wb,
        `budget_vs_actual_${filters.acadyr}${termSuffix}.xlsx`,
      );
    };

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Summary */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Budgeted"
                value={formatCurrency(budgetVsActualData.summary.totalBudgeted)}
                prefix="UGX"
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Spent"
                value={formatCurrency(budgetVsActualData.summary.totalSpent)}
                prefix="UGX"
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Overall Utilization"
                value={budgetVsActualData.summary.overallUtilization}
                suffix="%"
              />
            </Card>
          </Col>
        </Row>

        {/* Table */}
        <Card
          extra={
            <Button icon={<DownloadOutlined />} onClick={exportBudgetReport}>
              Export
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={budgetVsActualData.departments}
            rowKey="departmentId"
            scroll={{ x: 900 }}
            pagination={false}
          />
        </Card>
      </Space>
    );
  };

  // Revenue Tab Content - Redesigned
  const RevenueContent = () => {
    if (!revenueData) {
      return <Empty description="No revenue data available" />;
    }

    const creditEntries = revenueData.creditBreakdown?.entries || [];
    const creditColumns = [
      {
        title: "Student",
        key: "student",
        render: (_, entry) => (
          <div>
            <div>{entry.studentName || "-"}</div>
            <div style={{ color: "#8c8c8c", fontSize: 12 }}>
              {entry.studentSpc || "-"}
            </div>
          </div>
        ),
        width: 190,
      },
      {
        title: "Payment Reference",
        dataIndex: "paymentReference",
        key: "paymentReference",
        render: (reference) => reference || <Tag>Manual credit</Tag>,
        width: 170,
      },
      {
        title: "Original Credit",
        dataIndex: "originalAmount",
        key: "originalAmount",
        render: (amount) => `UGX ${formatCurrency(amount)}`,
        align: "right",
        width: 145,
      },
      {
        title: "Applied",
        dataIndex: "appliedAmount",
        key: "appliedAmount",
        render: (amount) => `UGX ${formatCurrency(amount)}`,
        align: "right",
        width: 130,
      },
      {
        title: "Remaining",
        dataIndex: "remainingAmount",
        key: "remainingAmount",
        render: (amount) => (
          <strong style={{ color: "#52c41a" }}>
            UGX {formatCurrency(amount)}
          </strong>
        ),
        align: "right",
        width: 145,
      },
      {
        title: "Term / Year",
        key: "period",
        render: (_, entry) =>
          `${entry.term || "No term"} / ${entry.acadyr || "-"}`,
        width: 150,
      },
      {
        title: "Reason",
        dataIndex: "reason",
        key: "reason",
        render: (reason) => reason || "-",
        width: 230,
      },
      {
        title: "Received",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (value) =>
          value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-",
        width: 155,
      },
    ];

    const transportTermColumns = [
      {
        title: "Term",
        dataIndex: "termName",
        key: "termName",
        render: (termName, row) => (
          <Space size="small">
            <span>{termName}</span>
            {row.isCurrent && <Tag color="blue">Current</Tag>}
          </Space>
        ),
      },
      {
        title: "Due",
        dataIndex: "totalDue",
        key: "totalDue",
        render: (amount) => `UGX ${formatCurrency(amount)}`,
        align: "right",
      },
      {
        title: "Paid",
        dataIndex: "totalPaid",
        key: "totalPaid",
        render: (amount) => (
          <span style={{ color: "#52c41a" }}>UGX {formatCurrency(amount)}</span>
        ),
        align: "right",
      },
      {
        title: "Outstanding",
        dataIndex: "outstanding",
        key: "outstanding",
        render: (amount) => (
          <strong style={outstandingValueStyle(amount)}>
            UGX {formatCurrency(amount)}
          </strong>
        ),
        align: "right",
      },
    ];

    // Export revenue data to Excel
    const exportRevenueReport = () => {
      // Create summary sheet
      const summaryData = [
        ["Revenue Report"],
        ["Generated:", dayjs().format("YYYY-MM-DD HH:mm")],
        ["Academic Year:", revenueFilters.acadyr || "All"],
        [
          "Date Range:",
          revenueFilters.dateRange
            ? `${revenueFilters.dateRange[0].format("YYYY-MM-DD")} to ${revenueFilters.dateRange[1].format("YYYY-MM-DD")}`
            : "All",
        ],
        [],
        [
          "Total Revenue Collected:",
          `UGX ${formatCurrency(revenueData.totalCollected)}`,
        ],
        [
          "Balance Brought Forward:",
          `UGX ${formatCurrency(revenueData.financialPosition?.balanceBroughtForward || 0)}`,
        ],
        [
          "Current Outstanding (Excl. B/F):",
          `UGX ${formatCurrency(revenueData.financialPosition?.currentOutstanding || 0)}`,
        ],
        [
          "Current Term Tuition Outstanding:",
          `UGX ${formatCurrency(revenueData.financialPosition?.currentTermOutstanding || 0)}`,
        ],
        [
          "Current Term Transport Outstanding:",
          `UGX ${formatCurrency(revenueData.financialPosition?.currentTermTransportOutstanding || 0)}`,
        ],
        [
          "Current Term Tuition Payable (Incl. B/F):",
          `UGX ${formatCurrency(revenueData.financialPosition?.currentTermPayable || 0)}`,
        ],
        [
          "Prepayments / Credits:",
          `UGX ${formatCurrency(revenueData.financialPosition?.prepaymentCredit || 0)}`,
        ],
        [
          "Gross Outstanding:",
          `UGX ${formatCurrency(revenueData.financialPosition?.grossOutstanding || 0)}`,
        ],
        [
          "Net Outstanding:",
          `UGX ${formatCurrency(revenueData.financialPosition?.netOutstanding || 0)}`,
        ],
        [],
        ["Fee Breakdown:"],
        [
          "Tuition - Due:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.tuition?.totalDue || 0)}`,
        ],
        [
          "Tuition - Paid:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.tuition?.totalPaid || 0)}`,
        ],
        [
          "Tuition - Outstanding:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.tuition?.outstanding || 0)}`,
        ],
        [
          "Other Standard School Fees - Due:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.otherSchool?.totalDue || 0)}`,
        ],
        [
          "Other Standard School Fees - Paid:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.otherSchool?.totalPaid || 0)}`,
        ],
        [
          "Other Standard School Fees - Outstanding:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.otherSchool?.outstanding || 0)}`,
        ],
        [
          "Custom Fees - Due:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.custom?.totalDue || 0)}`,
        ],
        [
          "Custom Fees - Paid:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.custom?.totalPaid || 0)}`,
        ],
        [
          "Custom Fees - Outstanding:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.custom?.outstanding || 0)}`,
        ],
        [
          "Transport - Due:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.transport?.totalDue || 0)}`,
        ],
        [
          "Transport - Paid:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.transport?.totalPaid || 0)}`,
        ],
        [
          "Transport - Outstanding:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.transport?.outstanding || 0)}`,
        ],
        ...(revenueData.feeBreakdown?.transport?.byTerm || []).flatMap(
          (item) => [
            [
              `Transport ${item.termName} - Due:`,
              `UGX ${formatCurrency(item.totalDue || 0)}`,
            ],
            [
              `Transport ${item.termName} - Paid:`,
              `UGX ${formatCurrency(item.totalPaid || 0)}`,
            ],
            [
              `Transport ${item.termName} - Outstanding:`,
              `UGX ${formatCurrency(item.outstanding || 0)}`,
            ],
          ],
        ),
        [
          "BOT - Due:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.bot?.totalDue || 0)}`,
        ],
        [
          "BOT - Paid:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.bot?.totalPaid || 0)}`,
        ],
        [
          "BOT - Outstanding:",
          `UGX ${formatCurrency(revenueData.feeBreakdown?.bot?.outstanding || 0)}`,
        ],
        [],
        ["Payment Methods:"],
        ...(revenueData.byPaymentMethod || []).map((item) => [
          item.method,
          `UGX ${formatCurrency(item.amount)}`,
          `${item.count} transactions`,
        ]),
        [],
        ["Channels:"],
        ...(revenueData.byChannel || []).map((item) => [
          item.channel,
          `UGX ${formatCurrency(item.amount)}`,
          `${item.count} transactions`,
        ]),
        [],
        ["Monthly Trend:"],
        ...(revenueData.monthlyTrend || []).map((item) => [
          item.month ? dayjs(item.month).format("MMMM YYYY") : "-",
          `UGX ${formatCurrency(item.amount)}`,
          `${item.count} transactions`,
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(summaryData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Revenue Summary");

      const creditExportData = creditEntries.map((entry) => ({
        Student: entry.studentName || "",
        SPC: entry.studentSpc || "",
        "Payment Reference": entry.paymentReference || "Manual credit",
        "Original Credit": parseFloat(entry.originalAmount || 0),
        "Applied to Fees": parseFloat(entry.appliedAmount || 0),
        "Remaining Credit": parseFloat(entry.remainingAmount || 0),
        "Academic Year": entry.acadyr || "",
        Term: entry.term || "",
        Reason: entry.reason || "",
        Received: entry.createdAt
          ? dayjs(entry.createdAt).format("YYYY-MM-DD HH:mm")
          : "",
      }));
      const creditWs = XLSX.utils.json_to_sheet(creditExportData, {
        header: [
          "Student",
          "SPC",
          "Payment Reference",
          "Original Credit",
          "Applied to Fees",
          "Remaining Credit",
          "Academic Year",
          "Term",
          "Reason",
          "Received",
        ],
      });
      XLSX.utils.book_append_sheet(wb, creditWs, "Credit Composition");
      XLSX.writeFile(wb, `revenue_report_${dayjs().format("YYYY-MM-DD")}.xlsx`);
    };

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Filters Card */}
        <Card title="Filters" size="small">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Academic Year"
                style={{ width: "100%" }}
                value={revenueFilters.acadyr}
                onChange={(value) =>
                  setRevenueFilters({ ...revenueFilters, acadyr: value })
                }
              >
                {academicYears.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                style={{ width: "100%" }}
                value={revenueFilters.dateRange}
                onChange={(dates) =>
                  setRevenueFilters({ ...revenueFilters, dateRange: dates })
                }
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={handleRefresh}
                loading={loading}
                style={{ width: "100%" }}
              >
                Refresh
              </Button>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportRevenueReport}
                style={{ width: "100%" }}
              >
                Export to Excel
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Total Revenue */}
        <Card size="small">
          <Statistic
            title={`Total Revenue Collected (${revenueFilters.acadyr || "All Years"})`}
            value={formatCurrency(revenueData.totalCollected)}
            prefix="UGX"
            valueStyle={{ color: "#52c41a", fontSize: "28px" }}
          />
          <div style={{ marginTop: "8px", fontSize: "12px", color: "#8c8c8c" }}>
            Valid payments received:{" "}
            {revenueFilters.dateRange
              ? `${revenueFilters.dateRange[0].format("YYYY-MM-DD")} to ${revenueFilters.dateRange[1].format("YYYY-MM-DD")}`
              : `all matching payments for ${revenueFilters.acadyr || "all years"}`}
          </div>
          {revenueData.feeBreakdownAcademicYear && (
            <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
              Fee balances below are cumulative assignments for{" "}
              {revenueData.feeBreakdownAcademicYear}.
            </div>
          )}
        </Card>

        <Card title="Financial Position" size="small">
          <Row gutter={[16, 16]}>
            <Col flex="1 1 180px">
              <Statistic
                title="Balance Brought Forward"
                value={formatCurrency(
                  revenueData.financialPosition?.balanceBroughtForward || 0,
                )}
                prefix="UGX"
                valueStyle={outstandingValueStyle(
                  revenueData.financialPosition?.balanceBroughtForward,
                )}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Current Outstanding (Excl. B/F)"
                value={formatCurrency(
                  revenueData.financialPosition?.currentOutstanding || 0,
                )}
                prefix="UGX"
                valueStyle={outstandingValueStyle(
                  revenueData.financialPosition?.currentOutstanding,
                )}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Current Term Tuition Outstanding"
                value={formatCurrency(
                  revenueData.financialPosition?.currentTermOutstanding || 0,
                )}
                prefix="UGX"
                valueStyle={outstandingValueStyle(
                  revenueData.financialPosition?.currentTermOutstanding,
                )}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Current Term Transport Outstanding"
                value={formatCurrency(
                  revenueData.financialPosition
                    ?.currentTermTransportOutstanding || 0,
                )}
                prefix="UGX"
                valueStyle={outstandingValueStyle(
                  revenueData.financialPosition
                    ?.currentTermTransportOutstanding,
                )}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Current Term Tuition Payable (Incl. B/F)"
                value={formatCurrency(
                  revenueData.financialPosition?.currentTermPayable || 0,
                )}
                prefix="UGX"
                valueStyle={outstandingValueStyle(
                  revenueData.financialPosition?.currentTermPayable,
                )}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Prepayments / Credits"
                value={formatCurrency(
                  revenueData.financialPosition?.prepaymentCredit || 0,
                )}
                prefix="UGX"
                valueStyle={{ color: "#52c41a" }}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Gross Outstanding"
                value={formatCurrency(
                  revenueData.financialPosition?.grossOutstanding || 0,
                )}
                prefix="UGX"
                valueStyle={outstandingValueStyle(
                  revenueData.financialPosition?.grossOutstanding,
                )}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Net Outstanding"
                value={formatCurrency(
                  revenueData.financialPosition?.netOutstanding || 0,
                )}
                prefix="UGX"
                valueStyle={outstandingValueStyle(
                  revenueData.financialPosition?.netOutstanding,
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card
          title={`Credit Composition (${revenueData.creditBreakdown?.entryCount || 0} open entries across ${revenueData.creditBreakdown?.studentCount || 0} students)`}
          size="small"
        >
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col flex="1 1 180px">
              <Statistic
                title="Original Credit"
                value={formatCurrency(
                  revenueData.creditBreakdown?.totalOriginal || 0,
                )}
                prefix="UGX"
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Applied from Open Credits"
                value={formatCurrency(
                  revenueData.creditBreakdown?.totalApplied || 0,
                )}
                prefix="UGX"
                valueStyle={{ color: "#1890ff" }}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title={`${revenueData.feeBreakdownAcademicYear || "Selected Year"} Available Credit`}
                value={formatCurrency(
                  revenueData.creditBreakdown?.totalRemaining || 0,
                )}
                prefix="UGX"
                valueStyle={{ color: "#52c41a" }}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title={`${revenueData.creditBreakdown?.currentTerm || "Current Term"} Credit Remaining`}
                value={formatCurrency(
                  revenueData.creditBreakdown?.currentTermRemaining || 0,
                )}
                prefix="UGX"
                valueStyle={{ color: "#389e0d" }}
              />
            </Col>
            <Col flex="1 1 180px">
              <Statistic
                title="Available Credit (All Time)"
                value={formatCurrency(
                  revenueData.creditBreakdown?.allTimeRemaining || 0,
                )}
                prefix="UGX"
                valueStyle={{ color: "#237804" }}
              />
            </Col>
          </Row>
          {creditEntries.length > 0 ? (
            <Table
              columns={creditColumns}
              dataSource={creditEntries}
              rowKey="id"
              size="small"
              scroll={{ x: 1315 }}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              summary={(entries) => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <strong>Remaining credit shown on this page</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong style={{ color: "#52c41a" }}>
                      UGX{" "}
                      {formatCurrency(
                        entries.reduce(
                          (total, entry) =>
                            total + Number(entry.remainingAmount || 0),
                          0,
                        ),
                      )}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} colSpan={3} />
                </Table.Summary.Row>
              )}
            />
          ) : (
            <Empty
              description={`No open credit entries for ${revenueFilters.acadyr}`}
            />
          )}
        </Card>

        <Alert
          type="info"
          showIcon
          message="Collection figures follow the payment filters; Due, Paid and Outstanding below are cumulative for the selected academic year."
        />

        {/* Revenue Breakdown by Type (Actual Collections) */}
        {revenueData.revenueBreakdown && (
          <Card title="Revenue Collected by Fee Type" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={12} md={6}>
                <Statistic
                  title="School Fees (SPC Payments)"
                  value={formatCurrency(
                    revenueData.revenueBreakdown?.tuition || 0,
                  )}
                  prefix="UGX"
                  valueStyle={{ fontSize: "14px", color: "#1890ff" }}
                />
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Statistic
                  title="Custom Fees"
                  value={formatCurrency(
                    revenueData.revenueBreakdown?.custom || 0,
                  )}
                  prefix="UGX"
                  valueStyle={{ fontSize: "14px", color: "#52c41a" }}
                />
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Statistic
                  title="Transport"
                  value={formatCurrency(
                    revenueData.revenueBreakdown?.transport || 0,
                  )}
                  prefix="UGX"
                  valueStyle={{ fontSize: "14px", color: "#faad14" }}
                />
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Statistic
                  title="BOT"
                  value={formatCurrency(revenueData.revenueBreakdown?.bot || 0)}
                  prefix="UGX"
                  valueStyle={{ fontSize: "14px", color: "#722ed1" }}
                />
              </Col>
              <Col xs={24}>
                <Statistic
                  title="Admission"
                  value={formatCurrency(
                    revenueData.revenueBreakdown?.admission || 0,
                  )}
                  prefix="UGX"
                  valueStyle={{ fontSize: "14px", color: "#eb2f96" }}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* Fee Breakdown */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Tuition Fees" size="small">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Due"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.tuition?.totalDue || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Paid"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.tuition?.totalPaid || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px", color: "#52c41a" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Outstanding"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.tuition?.outstanding || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenueData.feeBreakdown?.tuition?.outstanding,
                      { fontSize: "16px" },
                    )}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Other Standard School Fees" size="small">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Due"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.otherSchool?.totalDue || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Paid"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.otherSchool?.totalPaid || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px", color: "#52c41a" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Outstanding"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.otherSchool?.outstanding || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenueData.feeBreakdown?.otherSchool?.outstanding,
                      { fontSize: "16px" },
                    )}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Custom and Transport Fees */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Custom Fees" size="small">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Due"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.custom?.totalDue || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Paid"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.custom?.totalPaid || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px", color: "#52c41a" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Outstanding"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.custom?.outstanding || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenueData.feeBreakdown?.custom?.outstanding,
                      { fontSize: "16px" },
                    )}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Transport Fees" size="small">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Due"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.transport?.totalDue || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Paid"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.transport?.totalPaid || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px", color: "#52c41a" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Outstanding"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.transport?.outstanding || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenueData.feeBreakdown?.transport?.outstanding,
                      { fontSize: "16px" },
                    )}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* BOT and Admission Fees */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="BOT Fees" size="small">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Due"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.bot?.totalDue || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Paid"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.bot?.totalPaid || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px", color: "#52c41a" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Outstanding"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.bot?.outstanding || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenueData.feeBreakdown?.bot?.outstanding,
                      { fontSize: "16px" },
                    )}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Admission Fees" size="small">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Paid"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.admission?.totalPaid || 0,
                    )}
                    prefix="UGX"
                    valueStyle={{ fontSize: "16px", color: "#52c41a" }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Outstanding"
                    value={formatCurrency(
                      revenueData.feeBreakdown?.admission?.outstanding || 0,
                    )}
                    prefix="UGX"
                    valueStyle={outstandingValueStyle(
                      revenueData.feeBreakdown?.admission?.outstanding,
                      { fontSize: "16px" },
                    )}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Card
          title={`Transport Fees by Term (${revenueData.financialPosition?.currentTerm || "No current term"})`}
          size="small"
        >
          <Table
            columns={transportTermColumns}
            dataSource={revenueData.feeBreakdown?.transport?.byTerm || []}
            rowKey={(row) => row.termId || row.termName}
            pagination={false}
            size="small"
            locale={{
              emptyText:
                "No transport fees found for the selected academic year",
            }}
          />
        </Card>

        <Row gutter={[16, 16]}>
          {/* By Payment Method */}
          <Col xs={24} md={12}>
            <Card title="By Payment Method" size="small">
              <Table
                columns={[
                  {
                    title: "Payment Method",
                    dataIndex: "method",
                    key: "method",
                  },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    render: (val) => `UGX ${formatCurrency(val)}`,
                    align: "right",
                  },
                  {
                    title: "Count",
                    dataIndex: "count",
                    key: "count",
                    align: "right",
                  },
                ]}
                dataSource={revenueData.byPaymentMethod || []}
                rowKey="method"
                pagination={false}
                size="small"
              />
            </Card>
          </Col>

          {/* By Channel */}
          <Col xs={24} md={12}>
            <Card title="By Channel" size="small">
              <Table
                columns={[
                  { title: "Channel", dataIndex: "channel", key: "channel" },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    render: (val) => `UGX ${formatCurrency(val)}`,
                    align: "right",
                  },
                  {
                    title: "Count",
                    dataIndex: "count",
                    key: "count",
                    align: "right",
                  },
                ]}
                dataSource={revenueData.byChannel || []}
                rowKey="channel"
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
        </Row>

        {/* Monthly Trend */}
        <Card title="Monthly Trend" size="small">
          <Table
            columns={[
              {
                title: "Month",
                dataIndex: "month",
                key: "month",
                render: (val) => (val ? dayjs(val).format("MMMM YYYY") : "-"),
              },
              {
                title: "Amount",
                dataIndex: "amount",
                key: "amount",
                render: (val) => `UGX ${formatCurrency(val)}`,
                align: "right",
              },
              {
                title: "Transactions",
                dataIndex: "count",
                key: "count",
                align: "right",
              },
            ]}
            dataSource={revenueData.monthlyTrend || []}
            rowKey="month"
            pagination={false}
            size="small"
          />
        </Card>
      </Space>
    );
  };

  // Expenditure Tab Content - Redesigned
  const ExpenditureContent = () => {
    if (!expenditureData) {
      return <Empty description="No expenditure data available" />;
    }

    // Export expenditure data to Excel
    const exportExpenditureReport = () => {
      // Create summary sheet
      const summaryData = [
        ["Expenditure Report"],
        ["Generated:", dayjs().format("YYYY-MM-DD HH:mm")],
        ["Fiscal Year:", expenditureFilters.fiscalYear || "All"],
        [
          "Department:",
          departments.find(
            (d) => d.departmentId === expenditureFilters.departmentId,
          )?.name || "All",
        ],
        [],
        ["Summary:"],
        [
          "Total Requisitions:",
          expenditureData.summary?.totalRequisitions || 0,
        ],
        [
          "Total Requested:",
          `UGX ${formatCurrency(expenditureData.summary?.totalRequested || 0)}`,
        ],
        [
          "Total Approved:",
          `UGX ${formatCurrency(expenditureData.summary?.totalApproved || 0)}`,
        ],
        [
          "Total Fulfilled:",
          `UGX ${formatCurrency(expenditureData.summary?.totalFulfilled || 0)}`,
        ],
        [],
        ["By Status:"],
        ...(expenditureData.byStatus || []).map((item) => [
          item.status,
          `${item.count} requisitions`,
          `UGX ${formatCurrency(item.amount)}`,
        ]),
        [],
        ["By Priority:"],
        ...(expenditureData.byPriority || []).map((item) => [
          item.priority,
          `${item.count} requisitions`,
          `UGX ${formatCurrency(item.amount)}`,
        ]),
        [],
        ["By Department:"],
        ...(expenditureData.byDepartment || []).map((item) => [
          item.departmentName,
          `${item.count} requisitions`,
          `UGX ${formatCurrency(item.amount)}`,
        ]),
        [],
        ["Monthly Fulfilled:"],
        ...(expenditureData.monthlyFulfilled || []).map((item) => [
          item.month ? dayjs(item.month).format("MMMM YYYY") : "-",
          `${item.count} requisitions`,
          `UGX ${formatCurrency(item.amount)}`,
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(summaryData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Expenditure Summary");
      XLSX.writeFile(
        wb,
        `expenditure_report_${dayjs().format("YYYY-MM-DD")}.xlsx`,
      );
    };

    const statusColumns = [
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => {
          const colors = {
            Draft: "default",
            Submitted: "blue",
            "HoD Approved": "cyan",
            "Finance Approved": "purple",
            Procurement: "orange",
            Fulfilled: "green",
            Rejected: "red",
          };
          return <Tag color={colors[status] || "default"}>{status}</Tag>;
        },
      },
      { title: "Count", dataIndex: "count", key: "count", align: "right" },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
    ];

    const deptColumns = [
      {
        title: "Department",
        dataIndex: "departmentName",
        key: "departmentName",
      },
      {
        title: "Requisitions",
        dataIndex: "count",
        key: "count",
        align: "right",
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
    ];

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Filters Card */}
        <Card title="Filters" size="small">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Academic Year"
                style={{ width: "100%" }}
                value={expenditureFilters.fiscalYear}
                onChange={(value) =>
                  setExpenditureFilters({
                    ...expenditureFilters,
                    fiscalYear: value,
                  })
                }
              >
                {academicYears.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Department"
                style={{ width: "100%" }}
                allowClear
                value={expenditureFilters.departmentId}
                onChange={(value) =>
                  setExpenditureFilters({
                    ...expenditureFilters,
                    departmentId: value,
                  })
                }
              >
                {departments.map((d) => (
                  <Option key={d.departmentId} value={d.departmentId}>
                    {d.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={handleRefresh}
                loading={loading}
                style={{ width: "100%" }}
              >
                Refresh
              </Button>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportExpenditureReport}
                style={{ width: "100%" }}
              >
                Export to Excel
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Summary */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Card size="small">
              <Statistic
                title="Total Requisitions"
                value={expenditureData.summary?.totalRequisitions || 0}
                valueStyle={{ fontSize: "24px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small">
              <Statistic
                title="Total Requested"
                value={formatCurrency(
                  expenditureData.summary?.totalRequested || 0,
                )}
                prefix="UGX"
                valueStyle={{ fontSize: "16px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small">
              <Statistic
                title="Total Approved"
                value={formatCurrency(
                  expenditureData.summary?.totalApproved || 0,
                )}
                prefix="UGX"
                valueStyle={{ fontSize: "16px", color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small">
              <Statistic
                title="Total Fulfilled"
                value={formatCurrency(
                  expenditureData.summary?.totalFulfilled || 0,
                )}
                prefix="UGX"
                valueStyle={{ fontSize: "16px", color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {/* By Status */}
          <Col xs={24} md={12}>
            <Card title="By Status" size="small">
              <Table
                columns={statusColumns}
                dataSource={expenditureData.byStatus || []}
                rowKey="status"
                pagination={false}
                size="small"
              />
            </Card>
          </Col>

          {/* By Priority */}
          <Col xs={24} md={12}>
            <Card title="By Priority" size="small">
              <Table
                columns={[
                  {
                    title: "Priority",
                    dataIndex: "priority",
                    key: "priority",
                    render: (p) => {
                      const colors = {
                        Low: "default",
                        Medium: "blue",
                        High: "orange",
                        Urgent: "red",
                      };
                      return <Tag color={colors[p] || "default"}>{p}</Tag>;
                    },
                  },
                  {
                    title: "Count",
                    dataIndex: "count",
                    key: "count",
                    align: "right",
                  },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    render: (val) => `UGX ${formatCurrency(val)}`,
                    align: "right",
                  },
                ]}
                dataSource={expenditureData.byPriority || []}
                rowKey="priority"
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
        </Row>

        {/* By Department */}
        <Card title="By Department" size="small">
          <Table
            columns={deptColumns}
            dataSource={expenditureData.byDepartment || []}
            rowKey="departmentId"
            pagination={false}
            size="small"
          />
        </Card>

        {/* Monthly Fulfilled */}
        <Card title="Monthly Fulfilled" size="small">
          <Table
            columns={[
              {
                title: "Month",
                dataIndex: "month",
                key: "month",
                render: (val) => (val ? dayjs(val).format("MMMM YYYY") : "-"),
              },
              {
                title: "Count",
                dataIndex: "count",
                key: "count",
                align: "right",
              },
              {
                title: "Amount",
                dataIndex: "amount",
                key: "amount",
                render: (val) => `UGX ${formatCurrency(val)}`,
                align: "right",
              },
            ]}
            dataSource={expenditureData.monthlyFulfilled || []}
            rowKey="month"
            pagination={false}
            size="small"
          />
        </Card>
      </Space>
    );
  };

  // Payroll Tab Content
  const PayrollContent = () => {
    if (!payrollData) {
      return <Empty description="No payroll data available" />;
    }

    const isMonthFiltered = !!payrollFilters.month;

    // Export payroll data to Excel
    const exportPayrollReport = () => {
      const dept =
        departments.find((d) => d.departmentId === payrollFilters.departmentId)
          ?.name || "All";
      const timestamp = dayjs().format("YYYY-MM-DD HH:mm");
      const n = (v) => parseFloat(v || 0);

      let sheetRows, colWidths, merges;

      if (isMonthFiltered) {
        // ── PER PERSON MODE (month selected) ─────────────────────────────
        // 7 cols: Staff Name | Basic | Allowances | Deductions | Gross | Net | Status
        const monthLabel = payrollFilters.month.format("MMMM YYYY");
        const data = payrollData.byPerson || [];
        const tot = {
          basic: data.reduce((s, r) => s + n(r.basicSalary), 0),
          allowances: data.reduce((s, r) => s + n(r.allowances), 0),
          deductions: data.reduce((s, r) => s + n(r.deductions), 0),
          gross: data.reduce((s, r) => s + n(r.grossSalary), 0),
          net: data.reduce((s, r) => s + n(r.netSalary), 0),
        };

        sheetRows = [
          [`PAYROLL REPORT — ${monthLabel}`, "", "", "", "", "", ""],
          [""],
          [
            "Generated:",
            timestamp,
            "",
            "Month:",
            monthLabel,
            "",
            `Department: ${dept}`,
          ],
          [""],
          [
            "Staff Name",
            "Basic Salary (UGX)",
            "Allowances (UGX)",
            "Deductions (UGX)",
            "Gross Salary (UGX)",
            "Net Salary (UGX)",
            "Status",
          ],
          ...data.map((r) => [
            r.staffName,
            n(r.basicSalary),
            n(r.allowances),
            n(r.deductions),
            n(r.grossSalary),
            n(r.netSalary),
            r.status,
          ]),
          [
            "TOTAL",
            tot.basic,
            tot.allowances,
            tot.deductions,
            tot.gross,
            tot.net,
            `${data.length} staff`,
          ],
        ];

        colWidths = [
          { wch: 28 },
          { wch: 20 },
          { wch: 18 },
          { wch: 18 },
          { wch: 20 },
          { wch: 20 },
          { wch: 16 },
        ];
        merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];
      } else {
        // ── MONTHLY MODE (year selected) ──────────────────────────────────
        // 8 cols: Month | Basic | Allowances | Deductions | Gross | Net | # Employees | Staff Names
        const data = payrollData.monthly || [];
        const tot = {
          basic: data.reduce((s, r) => s + n(r.basicSalary), 0),
          allowances: data.reduce((s, r) => s + n(r.allowances), 0),
          deductions: data.reduce((s, r) => s + n(r.deductions), 0),
          gross: data.reduce((s, r) => s + n(r.grossSalary), 0),
          net: data.reduce((s, r) => s + n(r.netSalary), 0),
          employees: data.reduce((s, r) => s + (r.employeeCount || 0), 0),
        };

        sheetRows = [
          [
            `PAYROLL REPORT — ${payrollFilters.year}`,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
          ],
          [""],
          [
            "Generated:",
            timestamp,
            "",
            "Year:",
            payrollFilters.year,
            "",
            "Department:",
            dept,
          ],
          [""],
          [
            "Month",
            "Basic Salary (UGX)",
            "Allowances (UGX)",
            "Deductions (UGX)",
            "Gross Salary (UGX)",
            "Net Salary (UGX)",
            "# Employees",
            "Staff Names",
          ],
          ...data.map((r) => [
            r.month ? dayjs(r.month).format("MMMM YYYY") : "-",
            n(r.basicSalary),
            n(r.allowances),
            n(r.deductions),
            n(r.grossSalary),
            n(r.netSalary),
            r.employeeCount || 0,
            r.employeeNames?.join(", ") || "",
          ]),
          [
            "TOTAL",
            tot.basic,
            tot.allowances,
            tot.deductions,
            tot.gross,
            tot.net,
            tot.employees,
            "",
          ],
        ];

        colWidths = [
          { wch: 18 },
          { wch: 20 },
          { wch: 18 },
          { wch: 18 },
          { wch: 20 },
          { wch: 20 },
          { wch: 14 },
          { wch: 40 },
        ];
        merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }];
      }

      const ws = XLSX.utils.aoa_to_sheet(sheetRows);
      ws["!cols"] = colWidths;
      ws["!merges"] = merges;

      const wb = XLSX.utils.book_new();
      const sheetName = isMonthFiltered
        ? `Payroll ${payrollFilters.month.format("MMM YYYY")}`
        : `Payroll ${payrollFilters.year}`;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const fileName = isMonthFiltered
        ? `payroll_${payrollFilters.month.format("YYYY-MM")}_${dayjs().format("YYYYMMDD")}.xlsx`
        : `payroll_${payrollFilters.year}_${dayjs().format("YYYYMMDD")}.xlsx`;
      XLSX.writeFile(wb, fileName);
    };

    const statusColumns = [
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => {
          const colors = {
            Draft: "default",
            "Pending Approval": "blue",
            Approved: "cyan",
            Paid: "green",
            Rejected: "red",
          };
          return <Tag color={colors[status] || "default"}>{status}</Tag>;
        },
      },
      { title: "Count", dataIndex: "count", key: "count", align: "right" },
      {
        title: "Total Net Salary",
        dataIndex: "total",
        key: "total",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
    ];

    const personColumns = [
      { title: "Staff Name", dataIndex: "staffName", key: "staffName" },
      {
        title: "Basic Salary",
        dataIndex: "basicSalary",
        key: "basicSalary",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Allowances",
        dataIndex: "allowances",
        key: "allowances",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Deductions",
        dataIndex: "deductions",
        key: "deductions",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Gross Salary",
        dataIndex: "grossSalary",
        key: "grossSalary",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Net Salary",
        dataIndex: "netSalary",
        key: "netSalary",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (s) => {
          const colors = {
            Draft: "default",
            "Pending Approval": "blue",
            Approved: "cyan",
            Paid: "green",
            Rejected: "red",
          };
          return <Tag color={colors[s] || "default"}>{s}</Tag>;
        },
      },
    ];

    const monthlyColumns = [
      {
        title: "Month",
        dataIndex: "month",
        key: "month",
        render: (val) => (val ? dayjs(val).format("MMMM YYYY") : "-"),
      },
      {
        title: "Basic Salary",
        dataIndex: "basicSalary",
        key: "basicSalary",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Allowances",
        dataIndex: "allowances",
        key: "allowances",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Deductions",
        dataIndex: "deductions",
        key: "deductions",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Gross Salary",
        dataIndex: "grossSalary",
        key: "grossSalary",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
      },
      {
        title: "Net Salary",
        dataIndex: "netSalary",
        key: "netSalary",
        render: (val) => `UGX ${formatCurrency(val)}`,
        align: "right",
        width: 140,
      },
      {
        title: "Employees",
        dataIndex: "employeeCount",
        key: "employeeCount",
        align: "right",
      },
    ];

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Filters Card */}
        <Card title="Filters" size="small">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Year"
                style={{ width: "100%" }}
                value={
                  payrollFilters.month
                    ? payrollFilters.month.format("YYYY")
                    : payrollFilters.year
                }
                disabled={!!payrollFilters.month}
                onChange={(value) =>
                  setPayrollFilters({ ...payrollFilters, year: value })
                }
              >
                {academicYears.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <DatePicker
                picker="month"
                placeholder="Filter by Month"
                style={{ width: "100%" }}
                value={payrollFilters.month}
                allowClear
                onChange={(value) =>
                  setPayrollFilters({ ...payrollFilters, month: value })
                }
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Department"
                style={{ width: "100%" }}
                allowClear
                value={payrollFilters.departmentId}
                onChange={(value) =>
                  setPayrollFilters({ ...payrollFilters, departmentId: value })
                }
              >
                {departments.map((d) => (
                  <Option key={d.departmentId} value={d.departmentId}>
                    {d.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={handleRefresh}
                loading={loading}
                style={{ width: "100%" }}
              >
                Refresh
              </Button>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportPayrollReport}
                style={{ width: "100%" }}
              >
                Export to Excel
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Summary */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Records"
                value={payrollData.summary?.totalRecords || 0}
                valueStyle={{ fontSize: "24px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Gross Salary"
                value={formatCurrency(
                  payrollData.summary?.totalGrossSalary || 0,
                )}
                prefix="UGX"
                valueStyle={{ fontSize: "16px", color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Net Salary"
                value={formatCurrency(payrollData.summary?.totalNetSalary || 0)}
                prefix="UGX"
                valueStyle={{ fontSize: "16px", color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Additional Summary Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Basic Salary"
                value={formatCurrency(
                  payrollData.summary?.totalBasicSalary || 0,
                )}
                prefix="UGX"
                valueStyle={{ fontSize: "14px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Allowances"
                value={formatCurrency(
                  payrollData.summary?.totalAllowances || 0,
                )}
                prefix="UGX"
                valueStyle={{ fontSize: "14px", color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Deductions"
                value={formatCurrency(
                  payrollData.summary?.totalDeductions || 0,
                )}
                prefix="UGX"
                valueStyle={{ fontSize: "14px", color: "#ff4d4f" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Breakdown: Per Person (month selected) or Monthly */}
        {isMonthFiltered ? (
          <Card
            title={`Per Person Breakdown — ${payrollFilters.month.format("MMMM YYYY")}`}
            size="small"
          >
            <Table
              columns={personColumns}
              dataSource={payrollData.byPerson || []}
              rowKey="staffId"
              pagination={false}
              size="small"
              scroll={{ x: 1000 }}
            />
          </Card>
        ) : (
          <Card title="Monthly Breakdown" size="small">
            <Table
              columns={monthlyColumns}
              dataSource={payrollData.monthly || []}
              rowKey="month"
              pagination={false}
              size="small"
              scroll={{ x: 900 }}
            />
          </Card>
        )}

        <Row gutter={[16, 16]}>
          {/* By Status */}
          <Col xs={24} md={12}>
            <Card title="By Status" size="small">
              <Table
                columns={statusColumns}
                dataSource={payrollData.byStatus || []}
                rowKey="status"
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
        </Row>
      </Space>
    );
  };

  const tabItems = [
    {
      key: "dashboard",
      label: (
        <>
          <DashboardOutlined /> Dashboard
        </>
      ),
      children: <DashboardContent />,
    },
    {
      key: "budget",
      label: (
        <>
          <PieChartOutlined /> Budget vs Actual
        </>
      ),
      children: <BudgetVsActualContent />,
    },
    {
      key: "revenue",
      label: (
        <>
          <DollarOutlined /> Revenue
        </>
      ),
      children: <RevenueContent />,
    },
    {
      key: "expenditure",
      label: (
        <>
          <ShoppingCartOutlined /> Expenditure
        </>
      ),
      children: <ExpenditureContent />,
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {/* Header Card - Only for Dashboard and Budget tabs */}
      {(activeTab === "dashboard" || activeTab === "budget") && (
        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Academic Year"
                style={{ width: "100%" }}
                value={filters.acadyr}
                onChange={(value) => setFilters({ ...filters, acadyr: value })}
              >
                {academicYears.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>
            </Col>

            {activeTab === "budget" && (
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="All Terms"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.term}
                  onChange={(value) => setFilters({ ...filters, term: value })}
                >
                  <Option value="T1">Term 1</Option>
                  <Option value="T2">Term 2</Option>
                  <Option value="T3">Term 3</Option>
                </Select>
              </Col>
            )}

            <Col xs={24} sm={12} md={8}>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={handleRefresh}
                loading={loading}
                style={{ width: "100%" }}
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Main Content */}
      <Card>
        <Spin spinning={loading}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="large"
          />
        </Spin>
      </Card>
    </Space>
  );
};

export default FinancialReports;
